import React, { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useLessonStore } from '@/store/lessonStore'
import { apiClient } from '@/services/api'
import { Users, Wifi, WifiOff, UserMinus, Crown, Clock, MoreVertical } from 'lucide-react'
import type { Player } from '@/types'

interface PlayerManagerProps {
  expanded?: boolean
  isLoading?: boolean
}

export function PlayerManager({ expanded = false, isLoading = false }: PlayerManagerProps) {
  const { currentLesson, players, removePlayer } = useLessonStore()
  const [selectedPlayers, setSelectedPlayers] = useState<Set<string>>(new Set())
  const [showActions, setShowActions] = useState<string | null>(null)
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null)
  const menuBtnRefs = useRef<Record<string, HTMLButtonElement | null>>({})

  const connectedPlayers = players.filter(p => p.connection_status === 'connected')
  const disconnectedPlayers = players.filter(p => p.connection_status === 'disconnected')

  const handleKickPlayer = async (playerId: string) => {
    if (!currentLesson?.id) return

    try {
      const response = await apiClient.kickPlayer(currentLesson.id, playerId)
      if (response.success) {
        removePlayer(playerId)
      }
    } catch {
      // kick failed
    }
  }

  const handleBulkAction = async (action: 'kick' | 'mute') => {
    if (!currentLesson?.id || selectedPlayers.size === 0) return

    try {
      const response = await apiClient.bulkPlayerAction(
        currentLesson.id,
        action,
        Array.from(selectedPlayers)
      )
      if (response.success) {
        setSelectedPlayers(new Set())
      }
    } catch {
      // bulk action failed
    }
  }

  const togglePlayerSelection = (playerId: string) => {
    const newSelection = new Set(selectedPlayers)
    if (newSelection.has(playerId)) {
      newSelection.delete(playerId)
    } else {
      newSelection.add(playerId)
    }
    setSelectedPlayers(newSelection)
  }

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const hours = Math.floor(mins / 60)
    if (hours > 0) {
      return `${hours}h ${mins % 60}m`
    }
    return `${mins}m`
  }

  const PlayerCard = ({ player }: { player: Player }) => (
    <motion.div
      layout="position"
      initial={false}
      exit={{ opacity: 0, y: -20 }}
      className={`relative bg-surface rounded-lg p-4 transition-all ${
        selectedPlayers.has(player.id) ? 'ring-2 ring-blue-500' : ''
      }`}
    >
      {/* Selection checkbox */}
      <div className="absolute top-2 left-2">
        <input
          type="checkbox"
          checked={selectedPlayers.has(player.id)}
          onChange={() => togglePlayerSelection(player.id)}
          className="rounded border-theme-border bg-surface-hover text-blue-500 focus:ring-blue-500"
        />
      </div>

      {/* Player info */}
      <div className="ml-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {player.avatar_url ? (
              <img
                src={player.avatar_url}
                alt={player.display_name}
                className="w-8 h-8 rounded-full"
              />
            ) : (
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                {player.display_name.charAt(0).toUpperCase()}
              </div>
            )}

            <div>
              <div className="flex items-center gap-2">
                <span className="font-medium">{player.display_name}</span>
                {player.is_host && <Crown className="w-4 h-4 text-yellow-400" />}
              </div>
            </div>
          </div>

          {/* Connection status */}
          <div className="flex items-center gap-2">
            {player.connection_status === 'connected' ? (
              <Wifi className="w-4 h-4 text-green-400" />
            ) : (
              <WifiOff className="w-4 h-4 text-red-400" />
            )}

            {/* Actions menu */}
            <button
              ref={el => { menuBtnRefs.current[player.id] = el }}
              onClick={() => {
                if (showActions === player.id) {
                  setShowActions(null)
                  setMenuPos(null)
                } else {
                  const rect = menuBtnRefs.current[player.id]?.getBoundingClientRect()
                  if (rect) {
                    setMenuPos({ top: rect.bottom + 4, left: rect.right - 120 })
                  }
                  setShowActions(player.id)
                }
              }}
              className="p-1 hover:bg-surface-hover rounded transition-colors"
            >
              <MoreVertical className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Player stats */}
        {expanded && (
          <div className="mt-3 grid grid-cols-3 gap-4 text-sm">
            <div>
              <div className="text-theme-text-muted">Responses</div>
              <div className="font-medium">{player.response_count}</div>
            </div>
            <div>
              <div className="text-theme-text-muted">Session Time</div>
              <div className="font-medium">{formatDuration(player.session_duration)}</div>
            </div>
            <div>
              <div className="text-theme-text-muted">Last Seen</div>
              <div className="font-medium flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {new Date(player.last_seen_at).toLocaleTimeString()}
              </div>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  )

  return (
    <div className="bg-surface-deep rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5 text-[var(--color-primary)]" />
          <h3 className="ds-section-title flex items-center gap-2">
            Students ({isLoading ? (
              <>
                <div className="animate-spin w-3 h-3 border border-theme-text-muted border-t-transparent rounded-full"></div>
                ...
              </>
            ) : (
              players.length
            )})
          </h3>
        </div>

        {selectedPlayers.size > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-theme-text-muted">{selectedPlayers.size} selected</span>
            <button
              onClick={() => handleBulkAction('kick')}
              className="ds-btn ds-btn-danger ds-btn-sm"
            >
              Kick Selected
            </button>
          </div>
        )}
      </div>

      {/* Connection summary */}
      <div className="flex items-center gap-4 mb-4 text-sm">
        <div className="flex items-center gap-1 text-green-400">
          <Wifi className="w-4 h-4" />
          <span>{isLoading ? '...' : connectedPlayers.length} connected</span>
        </div>
        {!isLoading && disconnectedPlayers.length > 0 && (
          <div className="flex items-center gap-1 text-red-400">
            <WifiOff className="w-4 h-4" />
            <span>{disconnectedPlayers.length} disconnected</span>
          </div>
        )}
      </div>

      {/* Players list */}
      <div className={`space-y-3 ${expanded ? '' : 'max-h-96 overflow-y-auto'}`}>
        {isLoading ? (
          // Loading skeleton
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-surface rounded-lg p-4 animate-pulse">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-surface-hover rounded-full"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-surface-hover rounded w-24 mb-2"></div>
                    <div className="h-3 bg-surface-hover rounded w-16"></div>
                  </div>
                  <div className="w-4 h-4 bg-surface-hover rounded"></div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <AnimatePresence>
            {/* Connected players first */}
            {connectedPlayers.map(player => (
              <PlayerCard key={player.id} player={player} />
            ))}

            {/* Disconnected players */}
            {disconnectedPlayers.map(player => (
              <div key={player.id} className="opacity-60">
                <PlayerCard player={player} />
              </div>
            ))}

            {players.length === 0 && (
              <div className="text-center text-theme-text-muted py-8">
                <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No students yet</p>
                <p className="text-sm mt-1">Students will appear here when they join</p>
              </div>
            )}
          </AnimatePresence>
        )}
      </div>

      {/* Actions dropdown rendered fixed so it's not clipped by overflow */}
      {showActions && menuPos && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => { setShowActions(null); setMenuPos(null) }}
          />
          <div
            className="fixed bg-surface-deep border border-theme-border rounded-lg shadow-lg z-50 min-w-[120px]"
            style={{ top: menuPos.top, left: menuPos.left }}
          >
            <button
              onClick={() => {
                handleKickPlayer(showActions)
                setShowActions(null)
                setMenuPos(null)
              }}
              className="w-full px-3 py-2 text-left text-red-400 hover:bg-surface-hover rounded-lg transition-colors"
            >
              <UserMinus className="w-4 h-4 inline mr-2" />
              Kick
            </button>
          </div>
        </>
      )}
    </div>
  )
}